// src/common/components/cartModal/components/promoSelector/PromoSelector.tsx
import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdKeyboardArrowDown,
  MdEvent,
  MdVpnKey,
  MdStore,
  MdTag,
} from "react-icons/md";
import { useTheme } from "../../../../hooks/useTheme";
import { DeliveryType } from "../../../../types/enums";
import { CartItem, PromoInterface } from "../../../../types/types";
import styles from "./PromoSelector.module.scss";

interface PromoSelectorProps {
  applicablePromos: PromoInterface[];
  selectedPromo: PromoInterface | null;
  discountCode: string;
  cartItems: CartItem[];
  storeId?: number;
  deliveryType?: DeliveryType;
  onSelectPromo: (promo: PromoInterface | null) => void;
  onDiscountCodeChange: (code: string) => void;
}

const PromoSelector: React.FC<PromoSelectorProps> = ({
  applicablePromos,
  selectedPromo,
  discountCode,
  cartItems,
  storeId,
  deliveryType,
  onSelectPromo,
  onDiscountCodeChange,
}) => {
  const { theme } = useTheme();
  const [showPromoModal, setShowPromoModal] = useState(false);

  const handlePromoSelect = (promo: PromoInterface | null) => {
    onSelectPromo(promo);
    setShowPromoModal(false);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getPromoButtonText = () => {
    if (selectedPromo) {
      return `${selectedPromo.name} - ${selectedPromo.discount}%`;
    }

    if (applicablePromos.length > 0) {
      return `Доступно ${applicablePromos.length} промо-акций`;
    }

    return "Нет доступных промо-акций";
  };

  const requiresCode = selectedPromo?.useDiscountCode;


  
  return (
    <div className={styles.container}>
      {/* Кнопка выбора промо */}
      <button
        className={styles.promoButton}
        onClick={() => setShowPromoModal(true)}
        disabled={applicablePromos.length === 0 && !selectedPromo}
        style={{
          backgroundColor: theme.card,
          borderColor: theme.border,
        }}
      >
        <div className={styles.promoButtonContent}>
          {selectedPromo ? (
            <>
              <div
                className={styles.promoBadge}
                style={{ backgroundColor: `${theme.success}20` }}
              >
                <span
                  className={styles.promoBadgeText}
                  style={{ color: theme.success }}
                >
                  Промо
                </span>
              </div>
              <span
                className={styles.promoButtonText}
                style={{ color: theme.text }}
              >
                {selectedPromo.name}
              </span>
            </>
          ) : (
            <span
              className={styles.promoButtonPlaceholder}
              style={{ color: theme.textSecondary }}
            >
              {getPromoButtonText()}
            </span>
          )}
        </div>
        <MdKeyboardArrowDown size={24} color={theme.textSecondary} />
      </button>

      {/* Поле для ввода промокода */}
      {requiresCode && (
        <div className={styles.codeContainer}>
          <span className={styles.codeLabel} style={{ color: theme.text }}>
            Введите промокод:
          </span>
          <input
            type="text"
            className={styles.codeInput}
            value={discountCode}
            onChange={(e) => onDiscountCodeChange(e.target.value)}
            placeholder="XXXX-XXXX-XXXX"
            style={{
              backgroundColor: theme.card,
              borderColor: theme.border,
              color: theme.text,
            }}
          />
          <p className={styles.codeRequired} style={{ color: theme.warning }}>
            Для применения этой промо-акции необходим промокод
          </p>
        </div>
      )}

      {/* Модальное окно */}
      {showPromoModal &&
        createPortal(
          <div
            className={styles.modalOverlay}
            onClick={() => setShowPromoModal(false)}
          >
            <div
              className={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={styles.modalHeader}
                style={{ borderBottomColor: theme.border }}
              >
                <h3 className={styles.modalTitle} style={{ color: theme.text }}>
                  Выберите промо-акцию
                </h3>
                <button
                  className={styles.closeButton}
                  onClick={() => setShowPromoModal(false)}
                >
                  <MdClose size={24} color={theme.text} />
                </button>
              </div>

              <div className={styles.promoList}>
                {/* Вариант "Без промо" */}
                <button
                  className={`${styles.noPromoItem} ${!selectedPromo ? styles.selected : ""}`}
                  onClick={() => handlePromoSelect(null)}
                  style={{
                    backgroundColor: theme.card,
                    borderColor: !selectedPromo ? theme.primary : theme.border,
                  }}
                >
                  <span
                    className={styles.noPromoText}
                    style={{ color: theme.textSecondary }}
                  >
                    Без промо-акции
                  </span>
                </button>

                {/* Список промо */}
                {applicablePromos.map((promo) => (
                  <button
                    key={promo.id}
                    className={`${styles.promoItem} ${selectedPromo?.id === promo.id ? styles.selected : ""}`}
                    onClick={() => handlePromoSelect(promo)}
                    style={{
                      backgroundColor: theme.card,
                      borderColor:
                        selectedPromo?.id === promo.id
                          ? theme.primary
                          : theme.border,
                    }}
                  >
                    <div className={styles.promoHeader}>
                      <span
                        className={styles.promoName}
                        style={{ color: theme.text }}
                      >
                        {promo.name}
                      </span>
                      <span
                        className={styles.promoDiscount}
                        style={{ color: theme.success }}
                      >
                        {promo.discount}%
                      </span>
                    </div>

                    {promo.description && (
                      <p
                        className={styles.promoDescription}
                        style={{ color: theme.textSecondary }}
                      >
                        {promo.description}
                      </p>
                    )}

                    <div className={styles.promoDetails}>
                      {/* Показываем даты только если они важны */}
                      {promo.datesImportant && (
                        <div
                          className={styles.promoDetail}
                          style={{ backgroundColor: theme.overlay }}
                        >
                          <MdEvent size={14} color={theme.textSecondary} />
                          <span
                            className={styles.promoDetailText}
                            style={{ color: theme.textSecondary }}
                          >
                            {formatDate(promo.startDate)} -{" "}
                            {formatDate(promo.endDate)}
                          </span>
                        </div>
                      )}

                      {promo.useDiscountCode && (
                        <div
                          className={styles.promoDetail}
                          style={{ backgroundColor: theme.overlay }}
                        >
                          <MdVpnKey size={14} color={theme.textSecondary} />
                          <span
                            className={styles.promoDetailText}
                            style={{ color: theme.textSecondary }}
                          >
                            Нужен код
                          </span>
                        </div>
                      )}

                      {promo.useStores && promo.storesFor?.length > 0 && (
                        <div
                          className={styles.promoDetail}
                          style={{ backgroundColor: theme.overlay }}
                        >
                          <MdStore size={14} color={theme.textSecondary} />
                          <span
                            className={styles.promoDetailText}
                            style={{ color: theme.textSecondary }}
                          >
                            {promo.storesFor.length} магаз.
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}

                {applicablePromos.length === 0 && (
                  <div className={styles.noPromoItem}>
                    <MdTag
                      size={48}
                      color={theme.textSecondary}
                      className={styles.noPromoIcon}
                    />
                    <span
                      className={styles.noPromoText}
                      style={{ color: theme.textSecondary }}
                    >
                      Нет доступных промо-акций для этого заказа
                    </span>
                    <span
                      className={styles.noPromoSubtext}
                      style={{ color: theme.textSecondary }}
                    >
                      Промо-акции доступны только при самовывозе
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default PromoSelector;
